/**
 * Holds on a proposal card: a reason a proposal cannot be confirmed as it
 * stands, mirrored from the server's write rules so the care manager
 * sees the reason and the fix on the card instead of a failed confirm.
 *
 * The one hold today: an appointment whose date the note gave only in
 * words ("next Tuesday", "August 27th"). The reader keeps those words as
 * date_text and leaves scheduled_for empty; the record needs a real
 * date, so the card holds until one is set under Adjust.
 */

import type { ProposalWire } from '@/lib/notes';
import type { AddressRow, ClientPronouns } from '@/lib/clientRecord';
import { CARE_SETTING_PLACE_TYPES, IN_HOME_SUPPORT_STATUSES } from '@/lib/noteVocabulary';
import { pronounForms } from '@/lib/pronounForms';
import { formatAddress } from '@/lib/address';

export interface ProposalHold {
  /** The field that must be set before Confirm works; 'address' when the
   *  fix is on the Addresses card (a housing note). */
  field: 'scheduled_for' | 'occurred_on' | 'address';
  /** One line for the card. */
  message: string;
}

const APPOINTMENT_TYPES = new Set(['appointment_upcoming', 'appointment_past']);

function effective(p: ProposalWire, edits: Readonly<Record<string, string>>, field: string): string {
  const edit = edits[`${p.id}:${field}`];
  if (edit !== undefined) return edit.trim();
  const v = p.payload[field];
  return typeof v === 'string' ? v.trim() : '';
}

function dateMessage(p: ProposalWire): string {
  const words = p.payload['date_text'];
  return typeof words === 'string' && words.trim() !== ''
    ? `The note said "${words.trim()}", which isn't a full date. Set the date under Adjust.`
    : 'Needs a date before it goes on the record. Set it under Adjust.';
}

/**
/** What a housing note's card reads besides the proposal: the client's
 *  addresses and pronouns. */
export interface HousingContext {
  addresses: readonly AddressRow[];
  pronouns: ClientPronouns | null;
}

/** An address that is a care setting: typed as one, or, untyped, labelled
 *  a facility (every address before 0225 is untyped). The server's rule
 *  (repos/_noteProjections.ts isCareSetting). */
function isCareSetting(a: AddressRow): boolean {
  if (a.placeType != null) return CARE_SETTING_PLACE_TYPES.includes(a.placeType);
  return a.label === 'facility';
}

/** A housing confirm's outcome, as the server decides it
 *  (api/_shared/housingPlace.ts decideHousingPlace; one case table drives
 *  both tests). */
export type HousingOutcome =
  | { kind: 'wait'; reason: 'moved' | 'no_address' | 'new_home' }
  | { kind: 'return_home'; current_id: string | null; primary_id: string; type_primary: boolean }
  | { kind: 'type'; target_id: string }
  | { kind: 'mark_current'; target_id: string }
  | { kind: 'create' };

const POSTAL_PARTS = ['line1', 'line2', 'city', 'state', 'zip'] as const;
type HousingParts = Record<(typeof POSTAL_PARTS)[number], string>;

/** The address a housing card will send: its edits (`<id>:address.<part>`)
 *  over the proposal's own parts. The note's text alone is no parts. */
function housingParts(p: ProposalWire, edits: Readonly<Record<string, string>>): HousingParts | null {
  const raw = p.payload['address'];
  const base = raw !== null && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
  const touched = POSTAL_PARTS.some((k) => edits[`${p.id}:address.${k}`] !== undefined);
  if (!touched && base === null) return null;
  const part = (k: (typeof POSTAL_PARTS)[number]): string => {
    const e = edits[`${p.id}:address.${k}`];
    if (e !== undefined) return e.trim();
    const b = base?.[k];
    return typeof b === 'string' ? b.trim() : '';
  };
  return { line1: part('line1'), line2: part('line2'), city: part('city'), state: part('state'), zip: part('zip') };
}

/** The match key: the formatted text, case aside and spaces collapsed. */
function matchKey(a: { line1: string; line2?: string | null; city?: string; state?: string; zip?: string }): string {
  return formatAddress({ line1: a.line1, line2: a.line2 ?? '', city: a.city ?? '', state: a.state ?? '', zip: a.zip ?? '' })
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** The housing confirm's outcome (decideHousingPlace's rules, here). */
export function housingOutcome(p: ProposalWire, edits: Readonly<Record<string, string>>, ctx: HousingContext): HousingOutcome {
  const type = effective(p, edits, 'living_situation');
  const active = ctx.addresses.filter((a) => a.isActive);
  const primary = active.find((a) => a.isPrimary) ?? null;
  const current = active.find((a) => a.isCurrentLocation && !a.isPrimary) ?? null;
  const toHome = IN_HOME_SUPPORT_STATUSES.includes(type);
  const careType = CARE_SETTING_PLACE_TYPES.includes(type);
  const parts = housingParts(p, edits);

  if (parts !== null && parts.line1 !== '') {
    const key = matchKey(parts);
    if (primary !== null && matchKey(primary) === key) {
      return { kind: 'return_home', current_id: current?.id ?? null, primary_id: primary.id, type_primary: primary.placeType == null };
    }
    const match = active.find((a) => !a.isPrimary && matchKey(a) === key);
    if (match !== undefined) return { kind: 'mark_current', target_id: match.id };
    if (toHome) return { kind: 'wait', reason: 'new_home' };
    return { kind: 'create' };
  }

  if (current !== null && toHome && isCareSetting(current)) {
    return primary === null
      ? { kind: 'wait', reason: 'no_address' }
      : { kind: 'return_home', current_id: current.id, primary_id: primary.id, type_primary: primary.placeType == null };
  }
  const target = current ?? primary;
  if (target === null) return { kind: 'wait', reason: 'no_address' };
  if (target === primary && (careType || type === 'other')) return { kind: 'wait', reason: 'moved' };
  if (target.placeType != null && target.placeType !== type) return { kind: 'wait', reason: 'moved' };
  // An untyped care setting is read as one: a place that is not a care
  // setting is a move.
  if (target.placeType == null && isCareSetting(target) && !careType) return { kind: 'wait', reason: 'moved' };
  return { kind: 'type', target_id: target.id };
}

/** The wait's sentence, with the client's pronoun. */
export function housingWaitMessage(
  reason: 'moved' | 'no_address' | 'new_home',
  pronouns: ClientPronouns | null,
  address = '',
): string {
  const f = pronounForms(pronouns);
  if (reason === 'new_home') return `This says ${f.subject} moved home to ${address}. Add it on the Addresses card, then confirm.`;
  return reason === 'moved'
    ? `This says ${f.subject} moved. Add where ${f.subject} ${f.plural ? 'are' : 'is'} now on the Addresses card, then confirm.`
    : `Add ${f.possessive} address on the Addresses card, then confirm.`;
}

/** The parts the card will send, as one line (the hold and the outcome
 *  lines name it). */
function housingAddressLine(p: ProposalWire, edits: Readonly<Record<string, string>>): string {
  const parts = housingParts(p, edits);
  return parts === null ? '' : formatAddress(parts);
}

/**
 * The line a housing card shows when its confirm is the return home: what
 * Olive stops showing as where the client is now. Null otherwise.
 */
export function housingConfirmLine(
  p: ProposalWire,
  ctx: HousingContext,
  edits: Readonly<Record<string, string>> = {},
): string | null {
  if (p.proposal_type !== 'housing_change') return null;
  const outcome = housingOutcome(p, edits, ctx);
  if (outcome.kind !== 'return_home' || outcome.current_id === null) return null;
  const current = ctx.addresses.find((a) => a.id === outcome.current_id);
  if (current === undefined) return null;
  const f = pronounForms(ctx.pronouns);
  return `Back home. Olive stops showing ${current.line1} as where ${f.subject} ${f.plural ? 'are' : 'is'} now.`;
}

/**
 * The line a housing card shows when its confirm adds the address the
 * note gave, or makes a known one where the client is now (22l2). Null
 * otherwise.
 */
export function housingNewPlaceLine(
  p: ProposalWire,
  ctx: HousingContext,
  edits: Readonly<Record<string, string>> = {},
): string | null {
  if (p.proposal_type !== 'housing_change') return null;
  const outcome = housingOutcome(p, edits, ctx);
  if (outcome.kind !== 'create' && outcome.kind !== 'mark_current') return null;
  const f = pronounForms(ctx.pronouns);
  const where = `as where ${f.subject} ${f.plural ? 'are' : 'is'} now.`;
  const line = housingAddressLine(p, edits);
  return outcome.kind === 'create' ? `Olive adds ${line} ${where}` : `Olive shows ${line} ${where}`;
}

/**
 * The hold on a proposal given the care manager's edits so far, or null
 * when it can be confirmed. Same rule as the appointment writer: a
 * scheduled appointment needs scheduled_for; an attended or missed one
 * needs occurred_on. A housing note waits as the server would
 * (housingPlan) when the client's addresses are given.
 */
export function proposalHold(
  p: ProposalWire,
  edits: Readonly<Record<string, string>> = {},
  housing?: HousingContext,
): ProposalHold | null {
  if (p.proposal_type === 'housing_change') {
    if (housing === undefined) return null;
    const outcome = housingOutcome(p, edits, housing);
    return outcome.kind === 'wait'
      ? { field: 'address', message: housingWaitMessage(outcome.reason, housing.pronouns, housingAddressLine(p, edits)) }
      : null;
  }
  if (!APPOINTMENT_TYPES.has(p.proposal_type)) return null;
  const status =
    effective(p, edits, 'status') ||
    (p.proposal_type === 'appointment_upcoming' ? 'scheduled' : 'attended');
  if (status === 'scheduled' && effective(p, edits, 'scheduled_for') === '') {
    return { field: 'scheduled_for', message: dateMessage(p) };
  }
  if ((status === 'attended' || status === 'missed') && effective(p, edits, 'occurred_on') === '') {
    return { field: 'occurred_on', message: dateMessage(p) };
  }
  return null;
}
